import { Button, Flex, Modal, Tag, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import React, { type Key, type ReactNode, useReducer, useState } from 'react';
import { from, switchMap, throwError, of, tap, catchError, EMPTY } from 'rxjs';
import SpecialEventMealForm from './special-event-meal.form';
import { type SpecialEventMealsFiltersProps } from './special-event-meals.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { Meal } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation, useQuery } from '../../../jsonapi-provider';
import { Roles, Status } from '../../../types';
import utils from '../../../utils/utils';
import MealsFilter from '../meals/meals.filter';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Special Event Meal deleted successfully';

interface SpecialEventMealsPageState {
  filters?: SpecialEventMealsFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const serializeSort = (sort: SpecialEventMealsPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: SpecialEventMealsPageState['filters']): string | undefined => {
  const parts: string[] = ['isSpecialEventMeal==1'];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: SpecialEventMealsPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | SpecialEventMealsPageState['filters']
      | SpecialEventMealsPageState['pagination']
      | SpecialEventMealsPageState['sort'];
  }
): SpecialEventMealsPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as SpecialEventMealsPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as SpecialEventMealsPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as SpecialEventMealsPageState['sort'],
      };
  }
};

const SpecialEventMealsPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [mealToEdit, setMealToEdit] = useState<Meal>();
  const [specialEventMeal, { client }] = useMutation('special-event-meal');

  const meals = useQuery<Meal[]>([
    'meal',
    {
      filter: serializeFilters(debouncedState.filters),
      'fields[meal]': ['name', 'specialEventMeal'],
      'fields[special-event-meal]': [
        'startDate',
        'endDate',
        'availableWeekDays',
        'price',
        'availableSites',
        'status',
      ],
      include: ['specialEventMeal.availableSites'],
      page: {
        size: 10000,
        number: 1,
        totals: 1,
      },
      sort: serializeSort(debouncedState.sort),
    },
  ]);

  const columns: DataTableProps<Meal>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setMealToEdit(item);
          }}
        >
          {item.name}
        </Typography.Link>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Date Range',
      render: (value, item) => (
        <Typography.Paragraph>
          {item.specialEventMeal?.startDate.toString()} ~{' '}
          {item.specialEventMeal?.endDate.toString()}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'specialEventMeal.price',
      render: (value, item) => {
        return utils.currencyFormat(item.specialEventMeal?.price ?? 0);
      },
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Status',
      dataIndex: ['specialEventMeal.status'],
      render: (value, item) => (
        <Typography.Paragraph
          onClick={() => {
            setMealToEdit(item);
          }}
        >
          <Tag
            color={
              item.specialEventMeal?.status === Status.NeedUpdate
                ? 'warning'
                : item.specialEventMeal?.status === Status.Enabled
                ? 'success'
                : 'error'
            }
          >
            {item.specialEventMeal?.status === Status.NeedUpdate
              ? 'Need Update'
              : item.specialEventMeal?.status === Status.Enabled
              ? 'Enabled'
              : 'Disabled'}
          </Tag>
        </Typography.Paragraph>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
  ];

  return (
    <Flex gap="middle" vertical>
      <DataTable<Meal>
        title="Special event meals"
        description="List of meals to be served to customers at certain events"
        filtersNode={
          <MealsFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setMealToEdit(new Meal());
              }}
            >
              Add Special Event Meal
            </Button>
          </AuthorizedComponent>
        }
        dataList={meals}
        onChange={setState}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(client.delete(['special-event-meal', record.specialEventMeal!.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({ message: ' ', description: SUCCESS_DELETE_MESSAGE });
                        meals.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete special event meal',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={mealToEdit}>
        <ExtraContent>
          <SpecialEventMealForm
            meal={mealToEdit! ?? new Meal()}
            meals={meals.data?.filter((meal) => !meal.specialEventMeal) ?? []}
            onSuccess={(_meal) => {
              meals.refetch?.();
              setMealToEdit(undefined);
            }}
            onCancel={() => {
              setMealToEdit(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default SpecialEventMealsPage;
